import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  COMMUNITY_GROUP_NAME,
  CONTACT_EMAIL,
  INTERNAL_LIAISON,
  INTERNAL_LIAISON_EMAIL,
  REPRESENTATION_CATEGORY,
  REPRESENTATIVE_NAME,
} from 'constants/formFields';
import { OPTION_CATEGORY_COMMUNITY } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import { useCreateMaterialitySHCommunitiesMutation, useUpdateMaterialitySHCommunitiesMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import useCreateCommunitiesForm from './hooks/useCreateCommunitiesForm';

import type IMaterialSHCommunitiesItem from 'store/slices/materialAssessment/interfaces/IMaterialSHCommunitiesItem';
import type IFieldError from 'store/types/IFieldError';
import type { TCommunitiesCreateSchema } from 'utils/validators';
import type ICreateCommunities from './interfaces/ICreateCommunities';

import styles from './scss/CreateCommunities.module.scss';

function CreateCommunities({
  open, onClose, title, selectedItem, recordId, currentId, setCommunities, setOpenModal,
  vcmId,
}: ICreateCommunities) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createCommunity] = useCreateMaterialitySHCommunitiesMutation();
  const [updateCommunity] = useUpdateMaterialitySHCommunitiesMutation();
  const { setFieldsErrors } = useFieldsErrors();
  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
  } = useCreateCommunitiesForm({ isPageLoading: isLoading, selectedItem });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddCommunities = (community: IMaterialSHCommunitiesItem) => {
    if (currentId !== -1) {
      updateCommunity({
        id: currentId, maId: recordId, data: community, vcmId,
      }).then((request) => {
        toast.success(i18n.t('notifications.updatedCommunities'));
        if ('data' in request && request.data?.data) {
          setCommunities((prev) => {
            const index = prev.findIndex((item) => item.id === currentId);
            if (index !== -1) {
              const updatedCommunities = [...prev];
              updatedCommunities[index] = { ...updatedCommunities[index], ...request.data.data };
              return updatedCommunities;
            }
            return prev;
          });
        }
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TCommunitiesCreateSchema>({ errors, setError, fieldsName });
      });
    } else {
      createCommunity({
        data: community,
        maId: recordId,
        vcmId,
      }).then(() => {
        toast.success(i18n.t('notifications.createdCommunities'));
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TCommunitiesCreateSchema>({ errors, setError, fieldsName });
      });
    }
  };

  const onCreateInvestorSubmit = handleSubmit((data) => {
    onAddCommunities({
      name: data[COMMUNITY_GROUP_NAME],
      representationCategory: data[REPRESENTATION_CATEGORY]?.value || '',
      contactName: data[REPRESENTATIVE_NAME] || '',
      contactEmail: data[CONTACT_EMAIL] || '',
      internalLiaison: data[INTERNAL_LIAISON] || '',
      internalLiaisonEmail: data[INTERNAL_LIAISON_EMAIL] || '',
    });
    onCancel();
  });

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={COMMUNITY_GROUP_NAME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.communityGroupName')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                required
                placeholder={t('materialityAssessment.placeholder.typeHere')}
              />
            )}
          />
          <Controller
            control={control}
            name={REPRESENTATION_CATEGORY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.representationCategory')}
                placeholder={t('materialityAssessment.placeholder.selectDepartment')}
                options={OPTION_CATEGORY_COMMUNITY}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={REPRESENTATIVE_NAME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.representativeName')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                required
                placeholder={t('materialityAssessment.placeholder.typeHere')}
              />
            )}
          />
          <Controller
            control={control}
            name={CONTACT_EMAIL}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.contactEmail')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={INTERNAL_LIAISON}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.internalLiaison')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={INTERNAL_LIAISON_EMAIL}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.internalLiaisonEmail')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateCommunities;
